import { Input, InputProps } from '@chakra-ui/react';
import { ChangeEvent, ChangeEventHandler, forwardRef, ReactNode, useId } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  B2B_FIELD_CONTROL_BORDER_RADIUS,
  B2B_FIELD_CONTROL_BORDER_WIDTH,
  B2B_FIELD_CONTROL_HEIGHT_PX,
  B2B_FIELD_CONTROL_MAX_WIDTH,
  B2B_FIELD_CONTROL_PADDING_X,
  B2B_FIELD_CONTROL_PADDING_Y,
} from '../b2bFormControlTokens';
import { B2bFieldLayout } from '../B2bFieldLayout';
import { b2bFieldFocusVisible } from 'styles/colors';

export type B2bSelectOption = { value: string; label: string };

export type FieldSelectProps = Omit<
  InputProps,
  'id' | 'value' | 'defaultValue' | 'onChange' | 'children' | 'as'
> & {
  id?: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: readonly B2bSelectOption[];
  value?: string;
  /**
   * Canonical selected option `value`, or `undefined` when empty / cleared.
   * Maps to form state and `buildB2BRegistrationRequestBody` (omit empty optionals — no forced `""`).
   */
  onValueChange: (value: string | undefined) => void;
  /** First option with `value=""`; choosing it yields `undefined`. Use for optional selects. */
  allowEmpty?: boolean;
  /** Label for the empty option when `allowEmpty` is true. */
  emptyOptionLabel?: ReactNode;
  /**
   * Required UX: first row `value=""`, `disabled` + `hidden`, so nothing valid is selected until the user picks.
   * Ignored when `allowEmpty` is true.
   */
  requiredPlaceholder?: ReactNode;
  layoutDir?: 'ltr' | 'rtl';
};

const defaultEmptyOptionLabel = (
  <FormattedMessage id='b2b.registration.field.selectEmpty' defaultMessage='Select…' />
);

/**
 * Native `<select>` via Chakra `Input` for B2B forms. Values are controlled; clearing uses `undefined`, not `""`.
 */
export const FieldSelect = forwardRef<HTMLSelectElement, FieldSelectProps>(
  (
    {
      id: idProp,
      label,
      hint,
      error,
      options,
      value,
      onValueChange,
      allowEmpty = false,
      emptyOptionLabel,
      requiredPlaceholder,
      isDisabled,
      isRequired,
      isReadOnly,
      isInvalid,
      layoutDir,
      ...restInputProps
    },
    ref,
  ) => {
    const autoId = useId();
    const fieldId = idProp ?? `b2b-field-select-${autoId}`;

    const selectDir = layoutDir ?? 'ltr';
    const textAlign =
      layoutDir === 'rtl' ? ('right' as const) : layoutDir === 'ltr' ? ('start' as const) : 'start';

    const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value;
      onValueChange(next === '' ? undefined : next);
    };

    /** Chakra `Input` types `onChange` as input-only even when `as="select"`. */
    const handleChangeAsInput = handleChange as unknown as ChangeEventHandler<HTMLInputElement>;

    return (
      <B2bFieldLayout
        fieldId={fieldId}
        label={label}
        hint={hint}
        error={error}
        isDisabled={isDisabled}
        isRequired={isRequired}
        isReadOnly={isReadOnly}
        isInvalid={isInvalid}
      >
        <Input
          as='select'
          id={fieldId}
          ref={ref}
          value={value ?? ''}
          onChange={handleChangeAsInput}
          cursor='pointer'
          dir={selectDir}
          w='full'
          maxW={B2B_FIELD_CONTROL_MAX_WIDTH}
          display='block'
          h={B2B_FIELD_CONTROL_HEIGHT_PX}
          minH={B2B_FIELD_CONTROL_HEIGHT_PX}
          px={B2B_FIELD_CONTROL_PADDING_X}
          py={B2B_FIELD_CONTROL_PADDING_Y}
          fontSize='md'
          lineHeight='1.25'
          textAlign={textAlign}
          borderRadius={B2B_FIELD_CONTROL_BORDER_RADIUS}
          borderWidth={B2B_FIELD_CONTROL_BORDER_WIDTH}
          borderColor='dark.D150'
          _hover={{ borderColor: 'dark.D200' }}
          _focusVisible={{
            ...b2bFieldFocusVisible,
          }}
          {...restInputProps}
        >
          {allowEmpty ? (
            <option value=''>{emptyOptionLabel ?? defaultEmptyOptionLabel}</option>
          ) : null}
          {!allowEmpty && requiredPlaceholder != null ? (
            <option value='' disabled>
              {requiredPlaceholder}
            </option>
          ) : null}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Input>
      </B2bFieldLayout>
    );
  },
);

FieldSelect.displayName = 'FieldSelect';
